export interface IStructureTreePoint {
  id: number;
  trainId?: number;
  title: string;
  direction: number;
  userSortNum: number | null;
}

export type TAggregateCheckboxState = "checked" | "unchecked" | "indeterminate";

export const structureTreeNodeId = {
  train: (trainId: number) => `t-${trainId}`,
  point: (pointId: number) => `p-${pointId}`,
  parseTrainId: (nodeId: string): number | null => (nodeId.startsWith("t-") ? Number(nodeId.slice(2)) : null),
};

export enum MULTI_ANALYSIS_MEASURE_TYPE {
  SPECTRUM = "spectrum",
  SIGNAL_FORM = "signal",
  ORBIT = "orbit",
  POLAR_TREND = "polar",
}

export interface IMultiAnalysisActiveMeasureWindow {
  templateId: number | null;
  cellId: string;
  row: number;
  column: number;
  measureType: MULTI_ANALYSIS_MEASURE_TYPE;
}

export type MultiAnalysisTimeValue = Date | string | null | undefined;

export interface IMultiAnalysisTimeRange {
  start: MultiAnalysisTimeValue;
  end: MultiAnalysisTimeValue;
}

export interface IMultiAnalysisDataMark {
  key: string;
  idMeasure: number;
  date: string;
  measureDate: string;
  idPoint: number;
  pointName: string;
  measureName: string;
  measureDescription?: string | null;
  value: number;
  value2: number | null;
  alarmStatus: number;
  units: number;
  rotationFrequencyRpm: number | null;
  param1: number | null;
  param2: number | null;
}

export type TChartLayoutMode = "2d" | "3d";

export enum ROTATION_DIRECTION {
  UNDEFINED = -1,
  COUNTER_CLOCKWISE = 0,
  CLOCKWISE = 1,
}

export const SIGNAL_DISPLAY_UNIT = {
  ACCELERATION: 0,
  VELOCITY: 1,
  DISPLACEMENT: 2,
} as const;

export type TSignalDisplayUnit = (typeof SIGNAL_DISPLAY_UNIT)[keyof typeof SIGNAL_DISPLAY_UNIT];

export const SIGNAL_TIME_SCALE = {
  TIME: "time",
  REVOLUTIONS: "revolutions",
} as const;

export type TSignalTimeScale = (typeof SIGNAL_TIME_SCALE)[keyof typeof SIGNAL_TIME_SCALE];

export const SPECTRUM_FREQUENCY_SCALE = {
  HERTZ: "hertz",
  ORDERS: "orders",
} as const;

export type TSpectrumFrequencyScale = (typeof SPECTRUM_FREQUENCY_SCALE)[keyof typeof SPECTRUM_FREQUENCY_SCALE];

export const BODE_FREQUENCY_SCALE = {
  HERTZ: "hertz",
  RPM: "rpm",
} as const;

export type TBodeFrequencyScale = (typeof BODE_FREQUENCY_SCALE)[keyof typeof BODE_FREQUENCY_SCALE];

export const BODE_FREQUENCY_AXIS_SCALE = {
  MIN_MAX: "min-max",
  ZERO: "zero",
} as const;

export type TBodeFrequencyAxisScale = (typeof BODE_FREQUENCY_AXIS_SCALE)[keyof typeof BODE_FREQUENCY_AXIS_SCALE];

export const CASCADE_SORT_MODE = {
  RPM: "rpm",
  TIME: "time",
} as const;

export type TCascadeSortMode = (typeof CASCADE_SORT_MODE)[keyof typeof CASCADE_SORT_MODE];

export interface IMultiAnalysisOrbitBandpassFilter {
  lo: number;
  hi: number;
}

export interface IMultiAnalysisFrequencyProfileSelection {
  frequencyHz: number;
  nativeX: number;
}

export type TMultiAnalysisSyncableMeasureType =
  | MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM
  | MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM;

export interface IMultiAnalysisSyncCursorValue {
  physicalX: number;
  markerPhysicalX: number | null;
  locked: boolean;
}

export const isSyncableMeasureType = (
  measureType: MULTI_ANALYSIS_MEASURE_TYPE | null | undefined,
): measureType is TMultiAnalysisSyncableMeasureType =>
  measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM || measureType === MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM;

export const SYNC_CURSOR_LINE_COLOR = "#1976d2";

export interface IOrbitBandSelection {
  panelKey: string;
  markerNativeX: number | null;
  cursorNativeX: number | null;
  datasetIndex: number;
}

export interface IOrbitRevolutionSelection {
  panelKey: string;
  cursorNativeX: number;
  bandStartNativeX: number;
  bandEndNativeX: number;
  datasetIndex: number;
}

export interface IMultiAnalysisToolbarState {
  harmonicCursors: boolean;
  subHarmonicCursors: boolean;
  faultFrequencies: boolean;
  markerMode: boolean;
  syncCursors: boolean;
  syncSpectrumCursor: IMultiAnalysisSyncCursorValue | null;
  syncSignalCursor: IMultiAnalysisSyncCursorValue | null;
  frequencyProfile: boolean;
  frequencyProfileSelection: IMultiAnalysisFrequencyProfileSelection | null;
  signalDisplayUnits: TSignalDisplayUnit;
  signalTimeScale: TSignalTimeScale;
  spectrumFrequencyScale: TSpectrumFrequencyScale;
  bodeFrequencyScale: TBodeFrequencyScale;
  bodeFrequencyAxisScale: TBodeFrequencyAxisScale;
  cascadeSortMode: TCascadeSortMode;
  cascadeDisplay3D: boolean;
  cascadeShowTrend: boolean;
  cascadeMarkerFrequencyHz: number | null;
  cascadeMarkerRpm: number | null;
  cascadeProfileRpm: number | null;
  cascadeCanConvertUnits: boolean;
  showOrbitPoints: boolean;
  showNyquistAmplitudePhase: boolean;
  showNyquistRotationFrequency: boolean;
  orbitOverlay: boolean;
  eccentricity: boolean;
  orbitFilterOpen: boolean;
  orbitRadialClearance: number | null;
  orbitAppliedBins: readonly number[];
  orbitAppliedBandpassHz: IMultiAnalysisOrbitBandpassFilter | null;
  orbitShowOriginalForm: boolean;
  signalAppliedHarmonics: readonly number[];
  signalAppliedBandpassHz: IMultiAnalysisOrbitBandpassFilter | null;
  rotationDirection: ROTATION_DIRECTION;
  orbitBandConstruction: boolean;
  orbitRevolutionConstruction: boolean;
  orbitBandSelection: IOrbitBandSelection | null;
  orbitRevolutionSelection: IOrbitRevolutionSelection | null;
}

export const DEFAULT_TOOLBAR_STATE: IMultiAnalysisToolbarState = {
  harmonicCursors: false,
  subHarmonicCursors: false,
  faultFrequencies: false,
  markerMode: false,
  syncCursors: false,
  syncSpectrumCursor: null,
  syncSignalCursor: null,
  frequencyProfile: false,
  frequencyProfileSelection: null,
  signalDisplayUnits: SIGNAL_DISPLAY_UNIT.ACCELERATION,
  signalTimeScale: SIGNAL_TIME_SCALE.TIME,
  spectrumFrequencyScale: SPECTRUM_FREQUENCY_SCALE.HERTZ,
  bodeFrequencyScale: BODE_FREQUENCY_SCALE.RPM,
  bodeFrequencyAxisScale: BODE_FREQUENCY_AXIS_SCALE.MIN_MAX,
  cascadeSortMode: CASCADE_SORT_MODE.RPM,
  cascadeDisplay3D: false,
  cascadeShowTrend: true,
  cascadeMarkerFrequencyHz: null,
  cascadeMarkerRpm: null,
  cascadeProfileRpm: null,
  cascadeCanConvertUnits: true,
  showOrbitPoints: false,
  showNyquistAmplitudePhase: true,
  showNyquistRotationFrequency: false,
  orbitOverlay: false,
  eccentricity: false,
  orbitFilterOpen: false,
  orbitRadialClearance: null,
  orbitAppliedBins: [],
  orbitAppliedBandpassHz: null,
  orbitShowOriginalForm: false,
  signalAppliedHarmonics: [],
  signalAppliedBandpassHz: null,
  rotationDirection: ROTATION_DIRECTION.UNDEFINED,
  orbitBandConstruction: false,
  orbitRevolutionConstruction: false,
  orbitBandSelection: null,
  orbitRevolutionSelection: null,
};

export const DEFAULT_CELL_DISPLAY_CASCADE_3D = true;

export const buildCellDisplayCascade3DKey = (templateId: number, cellId: string): string => `${templateId}:${cellId}`;
